(ns practitest.frontend.tests.version-label-common
  (:require
   [ajax.core :as ajax]
   [clojure.set]
   [practitest.frontend.common.format-utils :as fu]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.common.mixpanel :as mixpanel]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.modal :as modal]
   [practitest.frontend.components.notifications :as notifications]
   [practitest.frontend.components.selector2 :as selector2]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as re-frame]))

(defn show-modal-event
  [state-key load-event]
  (fn [{:keys [db]} [_ & data-args]]
    {:db (assoc-in db [:form-states2 state-key :show-modal?] true)
     :dispatch (into load-event data-args)}))

(defn hide-modal-event
  [state-key]
  (fn [db _]
    (-> db
        (assoc-in [:form-states2 state-key :show-modal?] false)
        (assoc-in [:form-states2 state-key :selected-labels] #{})
        (assoc-in [:form-states2 state-key :selected-version] nil))))

(defn set-selection-event
  [state-key selection-key]
  (fn [db [_ selection]]
    (assoc-in db [:form-states2 state-key selection-key] selection)))

(defn extract-error-message
  [error default-message]
  (let [error-response (get-in error [:response])
        errors (:errors error-response)]
    (or (when errors
          (cond
            (map? errors)
            (let [[field-key messages] (first errors)]
              (str (name field-key) " " (first messages)))

            (sequential? errors)
            (first errors)

            (string? errors)
            errors))
        default-message)))

(defn labels-load-error-event [progress-key]
  (fn [{:keys [db]} [_ error]]
    (let [error-message (extract-error-message error (get-translation "test.versioning.label-load-error" "Error loading labels"))]
      {:db (update db :progress disj progress-key)
       :dispatch [::notifications/error2 error-message {:auto-close true}]})))

(defn create-label-event [on-success]
  (fn [{:keys [db]} [_ label-name]]
    {:db (update db :progress conj ::create-label)
     :http-xhrio
     {:method          :post
      :uri             (broutes/get-url "version_labels#create" {:p_id (subs/get-current-project-id db)})
      :params          {:label {:name label-name}}
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      on-success
      :on-failure      [::label-create-error]}}))

(defn label-created-event [state-key tracking-action]
  (fn [db [_ response]]
    (let [new-label (:label response)]
      (when tracking-action
        (mixpanel/track "Test Versioning" {"Action" tracking-action}))
      (-> db
          (update-in [:form-states2 state-key :available-labels] conj new-label)
          (update-in [:form-states2 state-key :selected-labels] (fnil conj #{}) new-label)
          (update :progress disj ::create-label)))))

(re-frame/reg-event-fx
  ::label-create-error
  (fn [{:keys [db]} [_ error]]
    (let [error-message (extract-error-message error (get-translation "test.versioning.label-create-error" "Error creating label"))]
      {:db (update db :progress disj ::create-label)
       :dispatch [::notifications/error2 error-message {:auto-close true}]})))

(defn labels-apply-error-event [progress-key]
  (fn [{:keys [db]} [_ error]]
    (let [error-message (extract-error-message error (get-translation "test.versioning.label-apply-error" "Error applying labels"))]
      {:db (update db :progress disj progress-key)
       :dispatch [::notifications/error2 error-message {:auto-close true}]})))

;; Subs
(defn show-modal-sub [state-key]
  (fn [db]
    (get-in db [:form-states2 state-key :show-modal?] false)))

(defn available-labels-sub [state-key]
  (fn [db]
    (get-in db [:form-states2 state-key :available-labels] [])))

(defn selected-labels-sub [state-key]
  (fn [db]
    (get-in db [:form-states2 state-key :selected-labels] #{})))

(re-frame/reg-event-fx
  ::fetch-version-labels
  (fn [{:keys [db]} [_ project-id test-id]]
    {:db (update db :progress conj ::fetch-version-labels)
     :http-xhrio
     {:method          :get
      :uri             (broutes/get-url "version_labels#test_labels"
                                        {:p_id project-id}
                                        {:id test-id})
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::version-labels-loaded]
      :on-failure      [::version-labels-load-error]}}))

(re-frame/reg-event-db
  ::version-labels-loaded
  (fn [db [_ response]]
    (-> db
        (assoc-in [:entity :test :version-labels :data] response)
        (update :progress disj ::fetch-version-labels))))

(re-frame/reg-event-db
  ::version-labels-load-error
  (fn [db [_ error]]
    (update db :progress disj ::fetch-version-labels)))

(defn version-label-modal
  [{:keys [title title-default
           description description-default
           hide-modal-event
           apply-event
           set-selection-event
           create-label-event
           show?
           available-labels
           selected-labels
           multi?
           placeholder
           can-create-labels?
           non-removable-items
           initial-labels
           is-valid-new-label-fn
           warning-component
           button-text button-text-default
           button-disabled?
           apply-args]}]
  [modal/bootstrap-modal
   {:is-open? show?
    :on-request-close #(re-frame/dispatch hide-modal-event)
    :close-button? true
    :centered? true
    :class "version-label-modal"}

   nil

   [:<>
    [:div.header
     (get-translation title title-default)
     [:span.close-button
      {:on-click #(re-frame/dispatch hide-modal-event)}
      [icons/mux-x]]]

    [:div.modal-content
     [:p.description
      (get-translation description description-default)]

     [selector2/new-select
      (merge
       {:field-type :labels
        :items available-labels
        :label-fn :name
        :key-fn :id
        :placeholder placeholder}
       (if multi?
         {:multi? true
          :tags? true
          :value selected-labels
          :on-change (fn [value]
                       (let [real-labels (remove #(string? (:id %)) value)
                             final-labels (if initial-labels
                                           (clojure.set/union (set real-labels) initial-labels)
                                           real-labels)]
                         (re-frame/dispatch (conj set-selection-event final-labels))))}
         {:multi? false
          :value selected-labels
          :on-change (fn [value]
                       (re-frame/dispatch (conj set-selection-event value)))})
       (when non-removable-items
         {:non-removable-items non-removable-items})
       (when (and can-create-labels? create-label-event)
         {:on-add-value (fn [new-label-text]
                         (let [validated-name (fu/validate-new-list-value! new-label-text)]
                           (when validated-name
                             (re-frame/dispatch (conj create-label-event validated-name)))))})
       (when is-valid-new-label-fn
         {:is-valid-new-tag-fn is-valid-new-label-fn})
       (when-not can-create-labels?
         {:no-create-permission-message (get-translation "test.versioning.no-create-label-permission"
                                                         "You can select existing labels only. Creating new labels requires Edit Labels permission.")}))]

     warning-component]

    [:div.modal-footer
     [buttons/button
      {:on-click #(re-frame/dispatch hide-modal-event)
       :text (get-translation "generic.button.cancel" "Cancel")}]
     [buttons/button
      {:on-click #(re-frame/dispatch (into apply-event apply-args))
       :primary? true
       :disabled? (if (fn? button-disabled?)
                   (button-disabled? selected-labels)
                   button-disabled?)
       :text (get-translation button-text button-text-default)}]]]

   nil])
